import { Row, Col, Button } from 'antd';
import { FC, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ModalWarningLeavePage } from 'components';
import { PaymentMethodSearch } from 'utils';
import { RootState, t_medical_institution } from '../../../types';
import { updateMedicalDetail } from '../MedicalInstitutionDetail/slice';
import { getCountFacilityCode, medicalNewItem, updateCountFacilityCode } from '../thunk';
import Wrap from './styles';

type Props = {
  nextStep: () => void;
  stepToPrevious: () => void;
};

const MedicalRegisterSecondStep: FC<Props> = ({ nextStep, stepToPrevious }) => {
  const dispatch = useDispatch();
  const { medicalInstitution, dataStoreMedicalFields } = useSelector((state: RootState) => state.medicalRegister);

  const getBillableMonth = useMemo(() => {
    return (
      (dataStoreMedicalFields &&
        dataStoreMedicalFields?.find(([_key, value]: [string, any]) => value.display_id === 'billable_month')[1]
          .options) ||
      []
    );
  }, [dataStoreMedicalFields]);

  const paymentMethod = useMemo(() => {
    return (
      dataStoreMedicalFields?.find(([_key, value]: [string, any]) => value.display_id === 'payment_method')[1]
        .options || []
    );
  }, [dataStoreMedicalFields]);

  const registerMedical = async () => {
    const facility_zip: string = medicalInstitution.facility_zip ? medicalInstitution.facility_zip : '';
    const billMonth = getBillableMonth.find((f: any) => f.value === medicalInstitution.billable_month);
    const payment = paymentMethod.find((g: any) => g.value === medicalInstitution.payment_method);

    let medicalInformation: t_medical_institution = {
      facility_zip: facility_zip,
      facility_name: medicalInstitution.facility_name,
      facility_address1: medicalInstitution.facility_address1,
      facility_address2: medicalInstitution.facility_address2,
      facility_tel: medicalInstitution.facility_tel,
      facility_name_for_invoice: medicalInstitution.facility_name_for_invoice,
      facility_address_for_invoice: medicalInstitution.facility_address_for_invoice,
      payment_method: payment && payment.option_id,
      cpap_unit_price: medicalInstitution.cpap_unit_price || undefined,
      cpap_unit_sp_price: medicalInstitution.cpap_unit_sp_price || undefined,
      psg_unit_price: medicalInstitution.psg_unit_price || undefined,
      simplicity_unit_price: medicalInstitution.simplicity_unit_price || undefined,
      billable_month: billMonth && billMonth.option_id,
      account_name: medicalInstitution.account_name,
      remark_for_remittance: medicalInstitution.remark_for_remittance,
      in_charge_of_sales: medicalInstitution.in_charge_of_sales,
    };

    const items: any = await dispatch(
      getCountFacilityCode(PaymentMethodSearch(medicalInstitution.payment_method || '')),
    );
    const codeNumber = items.payload[0] && (+items.payload[0].count + 1).toString();

    const newFcode =
      codeNumber &&
      (codeNumber.length === 1 ? '00' + codeNumber : codeNumber.length === 2 ? '0' + codeNumber : codeNumber);

    if (medicalInstitution.payment_method) {
      medicalInformation = { ...medicalInformation, facility_code: items.payload[0].prefix + '-' + newFcode };
    }

    const result: any = await dispatch(medicalNewItem({ item: medicalInformation, return_item_result: true }));
    if (result) {
      dispatch(
        updateMedicalDetail({
          ...medicalInformation,
          d_id: result.payload.item.d_id,
          i_id: result.payload.item.i_id,
          payment_method: medicalInstitution.payment_method,
          billable_month: billMonth.value,
        }),
      );
      if (medicalInformation.payment_method) {
        dispatch(updateCountFacilityCode({ ...items.payload[0], count: +codeNumber }));
      }
      nextStep();
    }
  };
  return (
    <Wrap>
      <div className="section">
        <div className="confirm-register">
          <Row className="mb-10">
            <Col className="standardBoldFont" span={5}>
              医療機関名
            </Col>
            <Col className="standardFont" span={19}>
              {medicalInstitution.facility_name}
            </Col>
          </Row>
          <Row className="mb-35">
            <Col span={5} />
            <Col className="standardFont" span={19}>
              <Row className="label">請求書出力用 請求書出力用 改行位置設定</Row>
              <Row className="textarea">{medicalInstitution.facility_name_for_invoice}</Row>
            </Col>
          </Row>
          <Row className="flex-align mb-10">
            <Col className="standardBoldFont" span={5}>
              所在地
            </Col>
            <Col className="standardFont" span={19}>
              <Row className="mb-10">
                〒{medicalInstitution.facility_zip}
                {medicalInstitution.facility_address1}
              </Row>
              <Row>{medicalInstitution.facility_address2}</Row>
            </Col>
          </Row>
          <Row className="mb-35 flex-align">
            <Col span={5} />
            <Col className="standardFont" span={19}>
              <Row className="label">請求書出力用 改行位置設定 （都道府県/市区町村/番地 マンション・ビル・建物）</Row>
              <Row className="textarea">{medicalInstitution.facility_address_for_invoice}</Row>
            </Col>
          </Row>
          <Row className="mb-35">
            <Col className="standardBoldFont" span={5}>
              電話番号
            </Col>
            <Col className="standardFont" span={19}>
              {medicalInstitution.facility_tel}
            </Col>
          </Row>
          <Row className="mb-40">
            <Col className="standardBoldFont" span={5}>
              支払い方法
            </Col>
            <Col className="standardFont" span={19}>
              {medicalInstitution.payment_method}
            </Col>
          </Row>
          <Row className="mb-40">
            <Col className="standardBoldFont" span={5}>
              CPAP単価
            </Col>
            <Col className="standardFont" span={19}>
              ¥ {medicalInstitution.cpap_unit_price ? medicalInstitution.cpap_unit_price.toLocaleString() : ''}
            </Col>
          </Row>
          <Row className="mb-40">
            <Col className="standardBoldFont" span={5}>
              CPAP単価特別料金
            </Col>
            <Col className="standardFont" span={19}>
              ¥ {medicalInstitution.cpap_unit_sp_price ? medicalInstitution.cpap_unit_sp_price.toLocaleString() : ''}
            </Col>
          </Row>
          <Row className="mb-40">
            <Col className="standardBoldFont" span={5}>
              出張PSG検査単価
            </Col>
            <Col className="standardFont" span={19}>
              ¥ {medicalInstitution.psg_unit_price ? medicalInstitution.psg_unit_price.toLocaleString() : ''}
            </Col>
          </Row>
          <Row className="mb-40">
            <Col className="standardBoldFont" span={5}>
              簡易PSG検査単価
            </Col>
            <Col className="standardFont" span={19}>
              ¥{' '}
              {medicalInstitution.simplicity_unit_price
                ? medicalInstitution.simplicity_unit_price.toLocaleString()
                : ''}
            </Col>
          </Row>
          <Row className="mb-40">
            <Col className="standardBoldFont" span={5}>
              請求可能未受診月数
            </Col>
            <Col className="standardFont" span={19}>
              {medicalInstitution.billable_month} ヶ月
            </Col>
          </Row>
          <Row className="mb-40">
            <Col className="standardBoldFont" span={5}>
              入金名
            </Col>
            <Col className="standardFont" span={19}>
              {medicalInstitution.account_name}
            </Col>
          </Row>
          <Row>
            <Col className="standardBoldFont" span={5}>
              入金リスト振込備考
            </Col>
            <Col className="standardFont" span={19}>
              {medicalInstitution.remark_for_remittance}
            </Col>
          </Row>
          <Row>
            <Col className="standardBoldFont" span={5}>
              営業担当
            </Col>
            <Col className="standardFont" span={19}>
              {medicalInstitution.in_charge_of_sales}
            </Col>
          </Row>
        </div>
        <div className="standardBoldFont confirm-register-form">
          この内容で登録します。よろしければ「登録する」をクリックしてください。
        </div>
        <div className="confirm-register-btn">
          <Button onClick={stepToPrevious} className="btn-cancel-save">
            戻る
          </Button>
          <Button onClick={registerMedical} className="btn-save">
            登録する
          </Button>
        </div>
      </div>
      <ModalWarningLeavePage />
    </Wrap>
  );
};
export default MedicalRegisterSecondStep;
